/**
 * Capture a thumbnail from a Three.js canvas
 */
export async function captureThumbnail(
  canvas: HTMLCanvasElement,
  options?: {
    width?: number;
    height?: number;
    quality?: number;
  },
): Promise<Blob | null> {
  const { width = 400, height = 300, quality = 0.8 } = options || {};

  try {
    // Create a temporary canvas for resizing
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;

    const ctx = tempCanvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get 2d context for thumbnail');
      return null;
    }

    // Calculate scaling to fit while maintaining aspect ratio
    const sourceAspect = canvas.width / canvas.height;
    const targetAspect = width / height;

    let sourceX = 0;
    let sourceY = 0;
    let sourceWidth = canvas.width;
    let sourceHeight = canvas.height;

    if (sourceAspect > targetAspect) {
      // Source is wider - crop sides
      sourceWidth = canvas.height * targetAspect;
      sourceX = (canvas.width - sourceWidth) / 2;
    } else if (sourceAspect < targetAspect) {
      // Source is taller - crop top/bottom
      sourceHeight = canvas.width / targetAspect;
      sourceY = (canvas.height - sourceHeight) / 2;
    }

    // Draw the scaled/cropped image
    ctx.drawImage(
      canvas,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      width,
      height,
    );

    // Convert to blob
    return new Promise((resolve) => {
      tempCanvas.toBlob((blob) => resolve(blob), 'image/png', quality);
    });
  } catch (error) {
    console.error('Error capturing thumbnail:', error);
    return null;
  }
}

/**
 * Find the Three.js canvas element in the DOM
 */
export function findThreeCanvas(): HTMLCanvasElement | null {
  // Look for canvas elements, prioritizing those likely to be Three.js
  const canvases = document.querySelectorAll('canvas');

  for (const canvas of canvases) {
    // Check if it's likely a WebGL canvas (Three.js uses WebGL)
    const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');
    if (gl) {
      return canvas;
    }
  }

  // Fallback to first canvas if no WebGL canvas found
  return canvases[0] || null;
}

/**
 * Capture thumbnail from the current view
 */
export async function captureCurrentView(options?: {
  width?: number;
  height?: number;
  quality?: number;
}): Promise<Blob | null> {
  const canvas = findThreeCanvas();
  if (!canvas) {
    console.warn('No Three.js canvas found for thumbnail capture');
    return null;
  }

  return captureThumbnail(canvas, options);
}

/**
 * Generate a placeholder thumbnail (solid color with text)
 */
export async function generatePlaceholderThumbnail(
  title: string,
  options?: {
    width?: number;
    height?: number;
    bgColor?: string;
    textColor?: string;
  },
): Promise<Blob | null> {
  const {
    width = 400,
    height = 300,
    bgColor = '#1e1e1e',
    textColor = '#666666',
  } = options || {};

  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Fill background
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, width, height);

    // Draw 3D cube icon
    ctx.strokeStyle = textColor;
    ctx.lineWidth = 2;

    const iconSize = 60;
    const centerX = width / 2;
    const centerY = height / 2 - 20;

    // Simple 3D cube shape
    ctx.beginPath();
    // Front face
    ctx.moveTo(centerX - iconSize / 2, centerY - iconSize / 4);
    ctx.lineTo(centerX + iconSize / 2, centerY - iconSize / 4);
    ctx.lineTo(centerX + iconSize / 2, centerY + iconSize / 2);
    ctx.lineTo(centerX - iconSize / 2, centerY + iconSize / 2);
    ctx.closePath();
    ctx.stroke();

    // Top face
    ctx.beginPath();
    ctx.moveTo(centerX - iconSize / 2, centerY - iconSize / 4);
    ctx.lineTo(centerX - iconSize / 4, centerY - iconSize / 2);
    ctx.lineTo(centerX + iconSize / 4, centerY - iconSize / 2);
    ctx.lineTo(centerX + iconSize / 2, centerY - iconSize / 4);
    ctx.stroke();

    // Side face
    ctx.beginPath();
    ctx.moveTo(centerX + iconSize / 2, centerY - iconSize / 4);
    ctx.lineTo(centerX + iconSize * 0.75, centerY);
    ctx.lineTo(centerX + iconSize * 0.75, centerY + iconSize / 2);
    ctx.lineTo(centerX + iconSize / 2, centerY + iconSize / 2);
    ctx.stroke();

    // Draw title (truncated)
    ctx.fillStyle = textColor;
    ctx.font = '14px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';

    const maxLength = 30;
    const displayTitle =
      title.length > maxLength ? title.slice(0, maxLength) + '...' : title;
    ctx.fillText(displayTitle, centerX, centerY + iconSize / 2 + 30);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png', 0.8);
    });
  } catch (error) {
    console.error('Error generating placeholder thumbnail:', error);
    return null;
  }
}
